// src/routes/Login.js

import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Avatar,
  Button,
  TextField,
  Link as MuiLink,
  Grid,
  Box,
  Typography,
  Container,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  CircularProgress
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { motion } from 'framer-motion';
import Footer from '../../components/layout/Footer';
import axios from '../../utils/axiosConfig';
import { showError } from '../../utils/errorHandler';
import { useAuth } from '../../hooks/useAuth';
import { SkeletonLoginForm } from '../../components/common/SkeletonCard';
import { liquidGlassCardDark } from '../../theme/designTokens';
import { bg, text as textColors, border, statusBg } from '../../utils/darkModeAdapter';
import { useTheme } from '@mui/material/styles';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState(null);
  const [checkingToken, setCheckingToken] = useState(true); // ← トークン検証中フラグ

  const { refreshUserInfo } = useAuth();
  
  // パスワードリセット関連の状態
  const [openPasswordResetDialog, setOpenPasswordResetDialog] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState('');
  
  const navigate = useNavigate();
  const theme = useTheme();
  const BACKEND_URL = process.env.REACT_APP_API_URL;

  // ======================
  // (A) 初回マウント時:
  //     1) /auth/me でログイン済みかチェック
  //     2) ログイン済みなら /basic-info をさらにチェックしてLanding or Homeへ
  // ======================
  useEffect(() => {
    // _skipInterceptor: true を指定して、axiosインターセプターのリフレッシュ処理をスキップ
    axios
      .get(`${BACKEND_URL}/auth/me`, { withCredentials: true, _skipInterceptor: true })
      .then(async (res) => {
        // Cookieのアクセストークンが有効 → ログイン済み
        console.log('Already logged in. Checking basic-info...');

        try {
          const infoRes = await axios.get(`${BACKEND_URL}/basic-info/`, {
            withCredentials: true,
          });
          // ここまで来たらプロフィールあり
          console.log('User has basic-info => navigate /home');
          navigate('/home');
        } catch (infoErr) {
          // 404ならプロフィールなし => Landingへ
          if (infoErr.response && infoErr.response.status === 404) {
            console.log('No basic-info => navigate /landing');
            navigate('/landing');
          } else {
            console.warn('Unexpected error checking basic-info:', infoErr);
            // 何か別のエラーなら、一旦ホームへ飛ばすかエラー表示するか判断
            // ここでは一例としてLandingに飛ばす
            navigate('/landing');
          }
        }
      })
      .catch((err) => {
        // Cookieなし or トークン無効 => 未ログイン => フォーム表示
        // 401エラーは正常な動作（未ログイン状態）なのでログ出力しない
        if (err.response?.status !== 401) {
          console.warn('Login check error:', err);
        }
      })
      .finally(() => {
        setCheckingToken(false); // チェック完了
      });
  }, [BACKEND_URL, navigate]);

  // チェック中はスケルトンローディングを表示
  if (checkingToken) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: bg(theme).default
        }}
      >
        <Container component="main" maxWidth="sm">
          <Paper
            elevation={3}
            sx={{
              p: 4,
              borderRadius: 3,
              backgroundColor: bg(theme).card,
              border: `1px solid ${border(theme).light}`,
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
            }}
          >
            <SkeletonLoginForm />
          </Paper>
        </Container>
      </Box>
    );
  }

  // ======================
  // (B) ログインフォーム送信
  // ======================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg(null); // エラーメッセージリセット

    try {
      // /auth/login にログイン情報をPOST
      // クッキーベース認証なので、サーバーが自動的にクッキーを設定する
      await axios.post(
        `${BACKEND_URL}/auth/login`,
        { email, password },
        { withCredentials: true }
      );

      console.log('Login success. Updating user info...');

      // AuthContextのユーザー情報を更新
      try {
        await refreshUserInfo();
        console.log('User info updated successfully');
      } catch (userErr) {
        console.warn('Failed to update user info:', userErr);
      }

      console.log('Checking basic-info...');
      // ログイン成功後 => 再度 /basic-info を確認して、あるかどうかで遷移先を分岐
      try {
        await axios.get(`${BACKEND_URL}/basic-info/`, {
          withCredentials: true,
        });
        // プロフィールあり => /home へ
        console.log('User has basic-info => navigate /home');
        navigate('/home');
      } catch (infoErr) {
        // 404 => 基本情報なし => /landing へ
        if (infoErr.response && infoErr.response.status === 404) {
          console.log('No basic-info => navigate /landing');
          navigate('/landing');
        } else {
          console.error('Error checking basic-info after login:', infoErr);
          // ここでは一旦 /home へ飛ばすか、別途エラー画面に飛ばすか判断
          navigate('/landing');
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      if (error.response?.status === 401) {
        setErrorMsg('メールアドレスまたはパスワードが間違っています');
      } else {
        setErrorMsg('ログインに失敗しました');
        showError(error);
      }
    }
  };

  // ======================
  // (C) パスワードリセット関連の処理
  // ======================
  const handleOpenPasswordResetDialog = () => {
    setOpenPasswordResetDialog(true);
    setResetEmail('');
    setResetError('');
    setResetSuccess(false);
  };

  const handleClosePasswordResetDialog = () => {
    setOpenPasswordResetDialog(false);
    setResetEmail('');
    setResetError('');
    setResetSuccess(false);
  };

  const handlePasswordResetSubmit = async () => {
    if (!resetEmail.trim()) {
      setResetError('メールアドレスを入力してください');
      return;
    }

    // 簡易メールバリデーション
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(resetEmail)) {
      setResetError('正しいメールアドレスを入力してください');
      return;
    }

    setResetSubmitting(true);
    setResetError('');

    try {
      const response = await axios.post(
        `${BACKEND_URL}/auth/request-password-reset`,
        { email: resetEmail },
        { withCredentials: true }
      );
      
      setResetSuccess(true);
      console.log('Password reset request sent successfully');
    } catch (error) {
      console.error('Password reset request error:', error);
      const errorMessage = error.response?.data?.detail || 
        'パスワードリセット要求の送信に失敗しました';
      setResetError(errorMessage);
      showError(error, errorMessage);
    } finally {
      setResetSubmitting(false);
    }
  };

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: bg(theme).default
      }}
    >
      {/* <Header /> */}

      <Box 
        sx={{ 
          flexGrow: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          py: { xs: 2, sm: 4 }
        }}
      >
        <Container component="main" maxWidth="sm">
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            sx={{ 
              textAlign: 'center', 
              mb: 4
            }}
          >
                        <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                letterSpacing: '0.5px',
                color: textColors(theme).secondary,
                mb: 1,
                fontSize: { xs: '1.8rem', sm: '2.125rem' }
               }}
            >
              AI人事にログインして
              <br />
                             採用管理を{' '}
               <Box
                 component="span"
                 sx={{
                   fontSize: '1.3em',
                   fontWeight: 'bold',
                   display: 'inline-block',
                   color: '#249BE0',
                 }}
               >
                 簡単
               </Box>
               に
            </Typography>
          </Box>

          <Paper
            elevation={3}
            sx={{
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: 3,
              backgroundColor: bg(theme).card,
              border: `1px solid ${border(theme).light}`,
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
            }}
          >
            <Avatar
              sx={{
                m: 1,
                width: 56,
                height: 56,
                backgroundColor: '#249BE0',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)'
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 28, color: 'white' }} />
            </Avatar>

            <Typography 
              component="h1" 
              variant="h5" 
              sx={{ 
                fontWeight: 600, 
                color: textColors(theme).primary,
                mb: 3,
                fontSize: { xs: '1.25rem', sm: '1.5rem' }
              }}
            >
              ログイン
            </Typography>

            {/* エラー表示 */}
            {errorMsg && (
              <Alert
                severity="error"
                sx={{
                  width: '100%',
                  mb: 2,
                  borderRadius: 2,
                  backgroundColor: statusBg(theme).error,
                  border: '1px solid #fecaca',
                  '& .MuiAlert-message': {
                    fontSize: '0.9rem',
                    color: '#dc2626'
                  },
                  '& .MuiAlert-icon': {
                    color: '#dc2626'
                  }
                }}
              >
                {errorMsg}
              </Alert>
            )}

            {/* フォーム */}
            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1, width: '100%' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="メールアドレス"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: bg(theme).subtle,
                    border: `1px solid ${border(theme).light}`,
                    '& fieldset': {
                      borderColor: border(theme).light,
                    },
                    '&:hover fieldset': {
                      borderColor: '#249BE0',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#249BE0',
                      borderWidth: 2,
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: textColors(theme).muted,
                    '&.Mui-focused': {
                      color: '#249BE0',
                    }
                  }
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="パスワード"
                type="password"
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: bg(theme).subtle,
                    border: `1px solid ${border(theme).light}`,
                    '& fieldset': {
                      borderColor: border(theme).light,
                    },
                    '&:hover fieldset': {
                      borderColor: '#249BE0',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#249BE0',
                      borderWidth: 2,
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: textColors(theme).muted,
                    '&.Mui-focused': {
                      color: '#249BE0',
                    }
                  }
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  mt: 2,
                  mb: 3,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: '1rem',
                  textTransform: 'none',
                  backgroundColor: '#249BE0',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: '#1976d2',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  },
                  '&:active': {
                    transform: 'translateY(0px)',
                  }
                }}
              >
                ログイン
              </Button>

              <Box sx={{ textAlign: 'center' }}>
                <MuiLink 
                  component="button"
                  type="button"
                  variant="body2" 
                  sx={{ 
                    color: '#249BE0',
                    textDecoration: 'none',
                    fontWeight: 500,
                    cursor: 'pointer',
                    '&:hover': {
                      textDecoration: 'underline',
                      color: '#1976d2'
                    }
                  }}
                  onClick={handleOpenPasswordResetDialog}
                >
                  パスワードをお忘れですか？
                </MuiLink>
              </Box>
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* パスワードリセットダイアログ */}
      <Dialog 
        open={openPasswordResetDialog} 
        onClose={handleClosePasswordResetDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${border(theme).light}`,
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            fontWeight: 600,
            color: textColors(theme).primary,
            pb: 1
          }}
        >
          パスワードリセット
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          {resetSuccess ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Alert 
                severity="success" 
                sx={{ 
                  mb: 2,
                  borderRadius: 2,
                  backgroundColor: statusBg(theme).success,
                  border: '1px solid #bbf7d0',
                  '& .MuiAlert-message': {
                    color: '#166534'
                  },
                  '& .MuiAlert-icon': {
                    color: '#16a34a'
                  }
                }}
              >
                パスワードリセットの要求を受け付けました
              </Alert>
              <DialogContentText sx={{ color: textColors(theme).muted, lineHeight: 1.6 }}>
                管理者に連絡いたしました。3営業日以内にメールまたは電話にてご連絡いたします。
                <br />
                お急ぎの場合は、直接お電話でお問い合わせください。
              </DialogContentText>
            </Box>
          ) : (
            <>
              <DialogContentText sx={{ mb: 2, color: textColors(theme).muted, lineHeight: 1.6 }}>
                登録されているメールアドレスを入力してください。
                管理者に連絡し、パスワードリセットの手続きをご案内いたします。
              </DialogContentText>
              
              {resetError && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                    backgroundColor: statusBg(theme).error,
                    border: '1px solid #fecaca',
                    '& .MuiAlert-message': {
                      color: '#dc2626'
                    },
                    '& .MuiAlert-icon': {
                      color: '#dc2626'
                    }
                  }}
                >
                  {resetError}
                </Alert>
              )}
              
              <TextField
                autoFocus
                margin="dense"
                id="reset-email"
                label="メールアドレス"
                type="email"
                fullWidth
                variant="outlined"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                disabled={resetSubmitting}
                placeholder="例：example@company.com"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: bg(theme).subtle,
                    '& fieldset': {
                      borderColor: border(theme).light,
                    },
                    '&:hover fieldset': {
                      borderColor: '#249BE0',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#249BE0',
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: textColors(theme).muted,
                    '&.Mui-focused': {
                      color: '#249BE0',
                    }
                  }
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          {resetSuccess ? (
            <Button 
              onClick={handleClosePasswordResetDialog}
              variant="contained"
              autoFocus
              sx={{
                borderRadius: 2,
                px: 3,
                textTransform: 'none',
                fontWeight: 600,
                backgroundColor: '#249BE0',
                '&:hover': {
                  backgroundColor: '#1976d2',
                }
              }}
            >
              閉じる
            </Button>
          ) : (
            <>
              <Button 
                onClick={handleClosePasswordResetDialog}
                sx={{
                  color: textColors(theme).muted,
                  textTransform: 'none',
                  fontWeight: 500,
                  borderRadius: 2,
                  px: 3,
                  '&:hover': {
                    backgroundColor: bg(theme).hover,
                  }
                }}
                disabled={resetSubmitting}
              >
                キャンセル
              </Button>
              <Button 
                onClick={handlePasswordResetSubmit}
                variant="contained"
                disabled={resetSubmitting || !resetEmail.trim()}
                startIcon={resetSubmitting ? <CircularProgress size={20} /> : null}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  textTransform: 'none',
                  fontWeight: 600,
                  backgroundColor: '#249BE0',
                  '&:hover': {
                    backgroundColor: '#1976d2',
                  },
                  '&:disabled': {
                    backgroundColor: '#94a3b8',
                  }
                }}
              >
                {resetSubmitting ? '送信中...' : '管理者に連絡'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Footer />
    </Box>
  );
}

export default Login;